import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../../auth/AuthProvider'
import { ConfirmDangerModal } from '../ConfirmDangerModal'
import { IconButton } from '../IconButton'
import { Modal } from '../Modal'
import { SortableTh } from '../SortableTh'
import { useTableSort } from '../../hooks/useTableSort'
import {
  formatTurnosResumo,
  TURNOS_COLUNAS,
  celulaMatricula,
  nivelTemDados,
  escolaTemTotal,
  sumTurnos,
  turnosTotaisEscola,
} from '../../lib/matriculasEscola'
import {
  TURNOS_ESCOLA_UI,
  emptyTurnosEscolaForm,
  escolaTemTurnosCadastrados,
  listarDivergenciasTurnosManuais,
  mensagemAlertaDivergenciasTurnosManuais,
  somaTurnosEscola,
  turnoEscolaHabilitado,
  turnosEscolaFormFromEscolaOuMatricula,
  turnosEscolaFormToPayload,
  turnosEscolaOuMatricula,
  type DivergenciaTurnoManual,
  type TurnoEscolaFieldKey,
  type TurnosEscolaForm,
} from '../../lib/turnosEscola'
import { importarMatriculasR065, verificarMesImportacao } from '../../lib/importarMatriculasR065'
import { parseR065Pdf, type EscolaR065Parsed } from '../../lib/parseR065Pdf'
import {
  montarPayloadEscolaForm,
  salvarEscolaComResponsaveis,
} from '../../lib/salvarEscolaComResponsaveis'
import { supabase } from '../../lib/supabase'
import type {
  Escola,
  MatriculaEscola,
  MatriculaTurnos,
  NivelEnsinoR065,
  Rota,
  Usuario,
  ZonaTipo,
} from '../../types/database'
import { NIVEIS_R065 } from '../../types/database'

const STORAGE_SORT = 'escolas-sort'

function normalizeForSearch(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
}

function turnosPreview(row: EscolaR065Parsed, nivel: NivelEnsinoR065) {
  if (nivel === 'infantil') return row.infantil
  if (nivel === 'fund_menor') return row.fundMenor
  if (nivel === 'fund_maior') return row.fundMaior
  return row.eja
}

/** Total R065 (M+T+N+I) — CEACT fica à parte na coluna. */
function totalR065Escola(e: EscolaRow): number {
  if (escolaTemTurnosCadastrados(e)) return somaTurnosEscola(e).total
  if (e.matricula) return turnosTotaisEscola(e.matricula).total
  return Math.max(0, (e.total_alunos || 0) - (e.alunos_ceact || 0))
}

const emptyForm = {
  nome: '',
  endereco: '',
  bairro: '',
  zona: 'urbana' as ZonaTipo,
}

type EscolaRow = Escola & {
  rota?: { nome: string }
  matricula?: MatriculaEscola | null
}

interface EscolasSectionProps {
  embedded?: boolean
}

function mesReferenciaAtual() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
}

export function EscolasSection({ embedded = false }: EscolasSectionProps) {
  const { canWrite, canDelete, user } = useAuth()
  const [escolas, setEscolas] = useState<EscolaRow[]>([])
  const [rotas, setRotas] = useState<Rota[]>([])
  const [responsaveisUsuarios, setResponsaveisUsuarios] = useState<Usuario[]>([])
  const [responsavelIds, setResponsavelIds] = useState<string[]>([])
  const [busca, setBusca] = useState('')
  const [filtroRota, setFiltroRota] = useState('')
  const [filtroZona, setFiltroZona] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Escola | null>(null)
  const [form, setForm] = useState(emptyForm)
  const [turnosForm, setTurnosForm] = useState<TurnosEscolaForm>(emptyTurnosEscolaForm())
  const [editingMatricula, setEditingMatricula] = useState<MatriculaEscola | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Escola | null>(null)
  const [pdfPreview, setPdfPreview] = useState<EscolaR065Parsed[] | null>(null)
  const [pdfFileName, setPdfFileName] = useState('')
  const [mesReferencia, setMesReferencia] = useState(mesReferenciaAtual())
  const [anoLetivo, setAnoLetivo] = useState(new Date().getFullYear())
  const [importando, setImportando] = useState(false)
  const [substituirMes, setSubstituirMes] = useState(false)
  const [divergenciasTurnosManuais, setDivergenciasTurnosManuais] = useState<DivergenciaTurnoManual[]>(
    [],
  )
  const [expandedNiveis, setExpandedNiveis] = useState<Set<NivelEnsinoR065>>(() => new Set())
  const hasLoadedRef = useRef(false)
  const writable = canWrite('escolas')
  const tableSort = useTableSort<EscolaRow>('codigo', 'asc', STORAGE_SORT)

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    setError(null)
    const [e, r, mat, respUsers] = await Promise.all([
      supabase.from('escolas').select('*, rota:rotas(nome)').order('codigo_semed', { ascending: true, nullsFirst: false }),
      supabase.from('rotas').select('*').order('nome'),
      supabase.from('v_matriculas_escola_atual').select('*'),
      supabase.from('usuarios').select('*').eq('perfil', 'responsavel_escola').eq('ativo', true).order('nome'),
    ])
    if (e.error || r.error) {
      setError(e.error?.message || r.error?.message || 'Erro')
    } else {
      const matByEscola = new Map((mat.data as MatriculaEscola[] | null)?.map((m) => [m.escola_id, m]) || [])
      const matByCodigo = new Map((mat.data as MatriculaEscola[] | null)?.map((m) => [m.codigo_semed, m]) || [])
      const rows = ((e.data as EscolaRow[]) || []).map((escola) => ({
        ...escola,
        matricula: matByEscola.get(escola.id) || (escola.codigo_semed ? matByCodigo.get(escola.codigo_semed) : null) || null,
      }))
      setEscolas(rows)
      setRotas((r.data as Rota[]) || [])
      setResponsaveisUsuarios((respUsers.data as Usuario[]) || [])
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    if (!embedded) document.title = 'Escolas · ISEN EDUCAE'
  }, [embedded])

  useEffect(() => {
    if (hasLoadedRef.current) return
    hasLoadedRef.current = true
    load()
  }, [load])

  const filtered = useMemo(() => {
    const q = normalizeForSearch(busca.trim())
    return escolas.filter((e) => {
      if (filtroRota && e.rota_id !== filtroRota) return false
      if (filtroZona && e.zona !== filtroZona) return false
      if (q && !normalizeForSearch(e.nome).includes(q)) return false
      return true
    })
  }, [escolas, busca, filtroRota, filtroZona])

  const filteredSorted = useMemo(() => {
    const accessors: Record<string, (e: EscolaRow) => string | number> = {
      nome: (e) => e.nome,
      codigo: (e) => e.codigo_semed || 0,
      total: (e) => totalR065Escola(e),
    }
    return tableSort.sortRows(filtered, accessors)
  }, [filtered, tableSort])

  const niveisVisiveis = useMemo(
    () =>
      NIVEIS_R065.filter(
        (n) =>
          nivelTemDados(filteredSorted, n.key) ||
          filteredSorted.some((e) => escolaTemTurnosCadastrados(e) && (turnosEscolaOuMatricula(e, e.matricula, n.key)?.total ?? 0) > 0),
      ),
    [filteredSorted],
  )

  const mostrarTotal = useMemo(() => escolaTemTotal(filteredSorted), [filteredSorted])
  const hasSubHeader = expandedNiveis.size > 0
  const headerRowSpan = hasSubHeader ? 2 : 1

  function toggleNivel(nivel: NivelEnsinoR065) {
    setExpandedNiveis((prev) => {
      const next = new Set(prev)
      if (next.has(nivel)) next.delete(nivel)
      else next.add(nivel)
      return next
    })
  }

  function renderTurnosCells(
    t: MatriculaTurnos | null,
    temMatricula: boolean,
    colunas: readonly ('M' | 'T' | 'N' | 'I' | 'Total')[] = TURNOS_COLUNAS,
  ) {
    return colunas.map((col) => {
      const valor =
        col === 'M' ? t?.m ?? 0 :
        col === 'T' ? t?.t ?? 0 :
        col === 'N' ? t?.n ?? 0 :
        col === 'I' ? t?.i ?? 0 :
        t ? sumTurnos(t) : 0
      return (
        <td key={col} className="col-num col-turno">
          {celulaMatricula(valor, temMatricula)}
        </td>
      )
    })
  }

  function renderNivelCells(e: EscolaRow, nivel: NivelEnsinoR065) {
    const turnos = turnosEscolaOuMatricula(e, e.matricula, nivel)
    const temDados = !!turnos && (turnos.total > 0 || !!e.matricula || escolaTemTurnosCadastrados(e))
    if (expandedNiveis.has(nivel)) {
      return renderTurnosCells(turnos, temDados)
    }
    return (
      <td className="col-num col-turno col-total-resumo">
        {celulaMatricula(turnos ? sumTurnos(turnos) : 0, temDados)}
      </td>
    )
  }

  function openNew() {
    setError(null)
    setEditing(null)
    setForm(emptyForm)
    setTurnosForm(emptyTurnosEscolaForm())
    setEditingMatricula(null)
    setResponsavelIds([])
    setModalOpen(true)
  }

  async function openEdit(escola: EscolaRow) {
    setError(null)
    setEditing(escola)
    setForm({
      nome: escola.nome,
      endereco: escola.endereco || '',
      bairro: escola.bairro || '',
      zona: escola.zona,
    })
    setTurnosForm(turnosEscolaFormFromEscolaOuMatricula(escola, escola.matricula))
    setEditingMatricula(escola.matricula ?? null)
    const { data, error: errResp } = await supabase
      .from('escola_responsaveis')
      .select('usuario_id')
      .eq('escola_id', escola.id)
    if (errResp) {
      // Sem os vínculos atuais, salvar apagaria todos os responsáveis da escola.
      setError(errResp.message)
      return
    }
    setResponsavelIds(((data || []) as { usuario_id: string }[]).map((r) => r.usuario_id))
    setModalOpen(true)
  }

  async function save() {
    if (!form.nome.trim()) {
      setError('Nome é obrigatório')
      return
    }
    try {
      // RPC atômica: escola + responsáveis na mesma transação (US-R03).
      await salvarEscolaComResponsaveis({
        escolaId: editing?.id ?? null,
        escola: montarPayloadEscolaForm({
          nome: form.nome,
          endereco: form.endereco,
          bairro: form.bairro,
          zona: form.zona,
          turnos: turnosEscolaFormToPayload(turnosForm),
          // Salvar o formulário de turnos marca preenchimento manual (US-R05).
          turnosManuais: true,
        }),
        responsavelIds,
        sincronizarResponsaveis: writable,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar escola')
      return
    }
    setModalOpen(false)
    load(true)
  }

  async function onPdfFile(file: File) {
    setError(null)
    setImportando(true)
    try {
      const parsed = await parseR065Pdf(file)
      if (!parsed.length) {
        setError('Nenhuma escola encontrada no PDF R065')
        return
      }
      setPdfPreview(parsed)
      setPdfFileName(file.name)
      setDivergenciasTurnosManuais(listarDivergenciasTurnosManuais(escolas, parsed))
      try {
        const substituir = await verificarMesImportacao(mesReferencia)
        setSubstituirMes(substituir)
      } catch {
        setSubstituirMes(false)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao ler PDF')
    } finally {
      setImportando(false)
    }
  }

  async function confirmarImportacao() {
    if (!pdfPreview?.length || !user?.id) return
    if (!/^\d{4}-\d{2}-01$/.test(mesReferencia)) {
      setError('Informe o mês de referência')
      return
    }
    if (!Number.isInteger(anoLetivo) || anoLetivo < 2000 || anoLetivo > 2100) {
      setError('Informe um ano letivo válido')
      return
    }
    setImportando(true)
    setError(null)
    try {
      await importarMatriculasR065({
        escolas: pdfPreview,
        mesReferencia,
        anoLetivo,
        arquivoNome: pdfFileName,
        userId: user.id,
      })
      setPdfPreview(null)
      setPdfFileName('')
      setSubstituirMes(false)
      setDivergenciasTurnosManuais([])
      load(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao importar matrículas')
    } finally {
      setImportando(false)
    }
  }

  if (loading && !escolas.length && !embedded) return <div className="loading">Carregando escolas…</div>


  return (
    <div className={`escolas-section${embedded ? ' escolas-section-embedded' : ''}`}>
      <div
        className={embedded ? undefined : 'page-header'}
        style={embedded ? { display: 'flex', justifyContent: 'flex-end', marginBottom: 12 } : undefined}
      >
        {!embedded && <h1>Escolas</h1>}
        <div className="page-actions">
          {writable && (
            <>
              <label className="btn btn-secondary" style={{ cursor: importando ? 'wait' : 'pointer' }}>
                {importando ? 'Lendo PDF…' : 'Importar PDF'}
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  hidden
                  disabled={importando}
                  onChange={(ev) => {
                    const f = ev.target.files?.[0]
                    if (f) onPdfFile(f)
                    ev.target.value = ''
                  }}
                />
              </label>
              <button type="button" className="btn btn-primary" onClick={openNew}>
                Nova escola
              </button>
            </>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="filters">
        <div className="field" style={{ minWidth: 220, flex: 1 }}>
          <label>Buscar escola</label>
          <input
            type="search"
            value={busca}
            placeholder="Digite o nome…"
            onChange={(ev) => setBusca(ev.target.value)}
            autoComplete="off"
          />
        </div>
        <div className="field">
          <label>Rota</label>
          <select value={filtroRota} onChange={(ev) => setFiltroRota(ev.target.value)}>
            <option value="">Todas</option>
            {rotas.map((r) => (
              <option key={r.id} value={r.id}>
                {r.nome}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Zona</label>
          <select value={filtroZona} onChange={(ev) => setFiltroZona(ev.target.value)}>
            <option value="">Todas</option>
            <option value="urbana">Urbana</option>
            <option value="rural">Rural</option>
          </select>
        </div>
        <div className="kpi-sub" style={{ alignSelf: 'center' }}>
          {filteredSorted.length} escolas
        </div>
      </div>

      <div className="escolas-table-shell">
        <div className="escolas-table-scroll">
          <table className="data-table data-table-escolas">
            <thead className="escolas-thead-sticky">
              <tr>
                <SortableTh
                  label="ID"
                  sortKey="codigo"
                  activeKey={tableSort.sortKey}
                  direction={tableSort.sortDir}
                  onSort={tableSort.toggleSort}
                  col="num"
                  rowSpan={headerRowSpan}
                />
                <SortableTh
                  label="Nome"
                  sortKey="nome"
                  activeKey={tableSort.sortKey}
                  direction={tableSort.sortDir}
                  onSort={tableSort.toggleSort}
                  col="name"
                  rowSpan={headerRowSpan}
                />
                <th rowSpan={headerRowSpan} className="col-text">
                  Rota
                </th>
                {niveisVisiveis.map((n) =>
                  expandedNiveis.has(n.key) ? (
                    <th key={n.key} colSpan={5} className="col-grupo col-grupo-expand">
                      <button type="button" className="col-grupo-btn" onClick={() => toggleNivel(n.key)}>
                        {n.label} <span aria-hidden>▾</span>
                      </button>
                    </th>
                  ) : (
                    <th key={n.key} rowSpan={headerRowSpan} className="col-num col-grupo-expand">
                      <button type="button" className="col-grupo-btn" onClick={() => toggleNivel(n.key)}>
                        {n.label} <span aria-hidden>▸</span>
                      </button>
                    </th>
                  ),
                )}
                {mostrarTotal && (
                  <SortableTh
                    label="Total"
                    sortKey="total"
                    activeKey={tableSort.sortKey}
                    direction={tableSort.sortDir}
                    onSort={tableSort.toggleSort}
                    col="num"
                    rowSpan={headerRowSpan}
                  />
                )}
                {writable && (
                  <th rowSpan={headerRowSpan} className="col-actions">
                    Ações
                  </th>
                )}
              </tr>
              {hasSubHeader && (
                <tr>
                  {niveisVisiveis
                    .filter((n) => expandedNiveis.has(n.key))
                    .flatMap((n) =>
                      TURNOS_COLUNAS.map((col) => (
                        <th key={`${n.key}-${col}`} className="col-num col-turno-head escolas-thead-sub">
                          {col}
                        </th>
                      )),
                    )}
                </tr>
              )}
            </thead>
            <tbody>
              {loading && !escolas.length ? (
                <tr>
                  <td colSpan={99} className="kpi-sub" style={{ textAlign: 'center', padding: 24 }}>
                    Carregando escolas…
                  </td>
                </tr>
              ) : (
                filteredSorted.map((e) => {
                  const temMatricula = !!e.matricula
                  const totalEscola = totalR065Escola(e)
                  const temCeact = (e.alunos_ceact || 0) > 0
                  return (
                    <tr key={e.id}>
                      <td className="col-num">{e.codigo_semed ?? ''}</td>
                      <td className="col-name">{e.nome}</td>
                      <td className="col-text">{e.rota?.nome ?? ''}</td>
                      {niveisVisiveis.map((n) => (
                        <Fragment key={`${e.id}-${n.key}`}>{renderNivelCells(e, n.key)}</Fragment>
                      ))}
                      {mostrarTotal && (
                        <td className="col-num">
                          <strong>{temMatricula || totalEscola > 0 ? totalEscola : ''}</strong>
                          {temCeact && (
                            <span className="kpi-sub" style={{ display: 'block', fontSize: '0.75rem' }}>
                              +{e.alunos_ceact} CEACT
                            </span>
                          )}
                        </td>
                      )}
                      {writable && (
                        <td className="col-actions">
                          <div className="table-actions">
                            <IconButton variant="edit" label={`Editar ${e.nome}`} onClick={() => openEdit(e)} />
                            {canDelete('escolas') && (
                              <IconButton
                                variant="delete"
                                label={`Excluir ${e.nome}`}
                                onClick={() => setDeleteTarget(e)}
                              />
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="escolas-table-footer">
          <div className="kpi-sub">
            {filteredSorted.length
              ? `${filteredSorted.length} escola(s)`
              : 'Nenhuma escola encontrada'}
          </div>
        </div>
      </div>

      <Modal
        open={modalOpen}
        title={editing ? 'Editar escola' : 'Nova escola'}
        size="lg"
        onClose={() => setModalOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>
              Cancelar
            </button>
            <button type="button" className="btn btn-primary" onClick={save}>
              Salvar
            </button>
          </>
        }
      >
        {error && <div className="alert alert-error">{error}</div>}
        <p className="kpi-sub" style={{ marginBottom: 12 }}>
          Matrículas são atualizadas mensalmente via PDF R065. Sem importação, informe os alunos por turno abaixo.
        </p>
        <div className="form-grid">
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label>Nome *</label>
            <input value={form.nome} onChange={(ev) => setForm({ ...form, nome: ev.target.value })} />
          </div>
          <div className="field">
            <label>Endereço</label>
            <input value={form.endereco} onChange={(ev) => setForm({ ...form, endereco: ev.target.value })} />
          </div>
          <div className="field">
            <label>Bairro</label>
            <input value={form.bairro} onChange={(ev) => setForm({ ...form, bairro: ev.target.value })} />
          </div>
          <div className="field">
            <label>Zona *</label>
            <select value={form.zona} onChange={(ev) => setForm({ ...form, zona: ev.target.value as ZonaTipo })}>
              <option value="urbana">Urbana</option>
              <option value="rural">Rural</option>
            </select>
          </div>
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label>Matrículas por modalidade e turno</label>
            <p className="kpi-sub" style={{ margin: '0 0 8px' }}>
              Informe a quantidade de alunos em cada turno. Infantil não possui turno noturno.
              {editingMatricula
                ? ' Há importação R065 nesta escola; ao salvar, o cadastro manual passa a prevalecer e a próxima importação não sobrescreve estes turnos.'
                : ' Sem importação, use este formulário como fonte dos alunos. Ao salvar, a importação R065 preservará estes turnos.'}
            </p>
            <div className="escola-turnos-grid">
              <table className="escola-turnos-table">
                <colgroup>
                  <col className="escola-turnos-col-modalidade" />
                  <col span={4} className="escola-turnos-col-turno" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Modalidade</th>
                    {TURNOS_ESCOLA_UI.map((t) => (
                      <th key={t.key} title={t.label}>
                        {t.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {NIVEIS_R065.map((nivel) => (
                    <tr key={nivel.key}>
                      <th scope="row">{nivel.label}</th>
                      {TURNOS_ESCOLA_UI.map((turno) => {
                        const field = `${nivel.key}_${turno.key}` as TurnoEscolaFieldKey
                        const habilitado = turnoEscolaHabilitado(nivel.key, turno.key)
                        return (
                          <td key={turno.key}>
                            <input
                              type="number"
                              min={0}
                              step={1}
                              disabled={!habilitado}
                              value={habilitado ? turnosForm[field] || '' : ''}
                              onChange={(ev) => {
                                const raw = ev.target.value
                                setTurnosForm((prev) => ({
                                  ...prev,
                                  [field]: raw === '' ? 0 : Math.max(0, Math.floor(Number(raw) || 0)),
                                }))
                              }}
                            />
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {writable && (
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label>Responsáveis da escola / CEACT</label>
              <p className="kpi-sub" style={{ margin: '0 0 8px' }}>
                Escolha um responsável na lista para adicionar. Pode incluir quantos precisar.
              </p>
              {responsaveisUsuarios.length ? (
                <>
                  <select
                    value=""
                    onChange={(ev) => {
                      const id = ev.target.value
                      if (!id) return
                      setResponsavelIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
                    }}
                  >
                    <option value="">Adicionar responsável…</option>
                    {responsaveisUsuarios
                      .filter((u) => !responsavelIds.includes(u.id))
                      .map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.nome} — {u.email}
                        </option>
                      ))}
                  </select>
                  {responsavelIds.length ? (
                    <ul className="escola-responsaveis-list">
                      {responsaveisUsuarios
                        .filter((u) => responsavelIds.includes(u.id))
                        .map((u) => (
                          <li key={u.id}>
                            <div>
                              <strong>{u.nome}</strong>
                              <span className="kpi-sub">{u.email}</span>
                            </div>
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              onClick={() =>
                                setResponsavelIds((prev) => prev.filter((id) => id !== u.id))
                              }
                            >
                              Remover
                            </button>
                          </li>
                        ))}
                    </ul>
                  ) : (
                    <p className="kpi-sub" style={{ marginTop: 8 }}>
                      Nenhum responsável vinculado ainda.
                    </p>
                  )}
                </>
              ) : (
                <p className="kpi-sub">Nenhum usuário com perfil responsável da escola cadastrado.</p>
              )}
            </div>
          )}
        </div>
      </Modal>

      <Modal
        open={!!pdfPreview}
        title="Importar matrículas — PDF R065"
        onClose={() => {
          setPdfPreview(null)
          setSubstituirMes(false)
          setDivergenciasTurnosManuais([])
        }}
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setPdfPreview(null)
                setDivergenciasTurnosManuais([])
              }}
            >
              Cancelar
            </button>
            <button type="button" className="btn btn-primary" onClick={confirmarImportacao} disabled={importando}>
              {importando ? 'Importando…' : substituirMes ? 'Substituir mês' : 'Confirmar importação'}
            </button>
          </>
        }
      >
        <div className="form-grid" style={{ marginBottom: 12 }}>
          <div className="field">
            <label>Mês de referência</label>
            <input
              type="month"
              value={mesReferencia.slice(0, 7)}
              onChange={(ev) => setMesReferencia(`${ev.target.value}-01`)}
            />
          </div>
          <div className="field">
            <label>Ano letivo</label>
            <input type="number" value={anoLetivo} onChange={(ev) => setAnoLetivo(Number(ev.target.value))} />
          </div>
        </div>
        {error && <div className="alert alert-error">{error}</div>}
        {substituirMes && (
          <div className="alert alert-error" style={{ marginBottom: 12 }}>
            Já existe importação para este mês. Confirmar irá substituir os dados.
          </div>
        )}
        {divergenciasTurnosManuais.length > 0 && (
          <div className="alert alert-warning" style={{ marginBottom: 12 }} role="alert">
            {mensagemAlertaDivergenciasTurnosManuais(divergenciasTurnosManuais)}
          </div>
        )}
        <p>
          <strong>{pdfPreview?.length || 0}</strong> escolas detectadas em <em>{pdfFileName}</em>
        </p>
        <div className="table-wrap" style={{ maxHeight: 320 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Unidade</th>
                {NIVEIS_R065.map((n) => (
                  <th key={n.key}>{n.label}</th>
                ))}
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {pdfPreview?.slice(0, 10).map((row) => (
                <tr key={row.codigo}>
                  <td className="col-num">{row.codigo}</td>
                  <td className="col-name">{row.nome}</td>
                  {NIVEIS_R065.map((n) => (
                    <td key={n.key} className="col-text" style={{ fontSize: '0.85em' }}>
                      {formatTurnosResumo(turnosPreview(row, n.key))}
                    </td>
                  ))}
                  <td className="col-num">{row.totalEscola}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(pdfPreview?.length || 0) > 10 && (
          <p className="kpi-sub">Exibindo 10 de {pdfPreview?.length} escolas.</p>
        )}
      </Modal>

      <ConfirmDangerModal
        open={!!deleteTarget}
        title="Excluir escola"
        actionName="EXCLUIR ESCOLA"
        description={`Isso excluirá permanentemente "${deleteTarget?.nome}".`}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          if (!deleteTarget) return
          const { error: err } = await supabase.from('escolas').delete().eq('id', deleteTarget.id)
          if (err) {
            const msg = err.message || ''
            if (/foreign key|violates foreign key|still referenced/i.test(msg)) {
              throw new Error(
                'Não é possível excluir: há entregas, estoque ou planejamentos vinculados a esta escola.',
              )
            }
            throw new Error(msg || 'Falha ao excluir escola')
          }
          load(true)
        }}
      />
    </div>
  )
}
